/** @format */

import {
	Component,
	Input,
	Output,
	EventEmitter,
	Renderer2,
	Inject,
	OnChanges,
	SimpleChanges,
	OnDestroy,
	HostListener,
	OnInit
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { BreakPointNew } from '@shared/data-access/enums';
import { DeviceService } from '@dcsg-ngx-ecommerce/core/service/device.service';
import { AnalyticsService } from '@dcsg-ngx-ecommerce/core';
import { KeyValue } from '@shared/data-access/interfaces';

@Component({
	selector: 'homefield-side-panel',
	templateUrl: './side-panel.component.html',
	styleUrls: ['./side-panel.component.scss']
})
export class HomefieldSidePanelComponent implements OnChanges, OnDestroy, OnInit {
	/**
	 * @description Boolean representing if the side panel is open or not.
	 */
	@Input() showSidePanel = false;

	/**
	 * @description Number representing the duration of the side panel transition in milliseconds.
	 */
	@Input() duration = 500;

	/**
	 * @description Number representing the width of the side panel when opened as a
	 * percentage relative to the parent container.
	 */
	@Input() width = 40;

	/**
	 * @description String representing which direction the panel will be positioned at.
	 */
	@Input() side = 'right';

	/**
	 * @description String representing which direction the panel will be positioned at.
	 */
	@Input() modalName = '';

	/**
	 * @description Event Emitter that is fired when the panel is closed.
	 */
	@Output() sidePanelIsOpen = new EventEmitter<boolean>();

	panelStyle: KeyValue<string> | null = null;

	panelHeaderClass = '';
	/**
	 * @description String representing the css class that will be added to the side panel.
	 */
	@Input() panelContentClass = '';

	/**
	 * @description String representing the css class that will be added to the side panel body.
	 */
	@Input() panelBodyClass = '';

	/**
	 * @description
	 *
	 * isMobile boolean will be true if it is mobile veiw else false.
	 */
	isMobile: boolean;

	constructor(
		private renderer: Renderer2,
		@Inject(DOCUMENT) private document,
		private deviceService: DeviceService,
		public analyticsService: AnalyticsService
	) {}

	ngOnInit() {
		this.onResize();
	}

	/**
	 * @description Detects when the showSidePanel value changes and toggles the scrollbar.
	 */
	ngOnChanges(changes: SimpleChanges): void {
		if (
			changes.showSidePanel !== undefined &&
			changes.showSidePanel.currentValue !== undefined
		) {
			this.toggleScrollBar(changes.showSidePanel.currentValue);
		}
		if (this.showSidePanel) {
			this.getPanelStyle();

			if (this.modalName !== '') {
				/*
				 * Analytics Event
				 */
				// Set the modal name
				this.analyticsService.setModalName(this.modalName);

				// Set the Analytics event type
				this.analyticsService.setType('event', 'ModalOpen');

				// Trigger the Analytics update
				this.analyticsService.triggerAnalyticsEvent('reporting:update');
			}

			this.renderer.addClass(this.document.body, 'side-panel-open');
		} else {
			this.renderer.removeClass(this.document.body, 'side-panel-open');
		}
	}

	ngOnDestroy() {
		this.showSidePanel = false;
		this.toggleScrollBar(this.showSidePanel);
	}

	/**
	 * @description Closes the side panel and emits a sidePanelIsOpen event
	 */
	closePanel() {
		this.showSidePanel = false;
		this.sidePanelIsOpen.emit(false);
	}

	/**
	 * @description Hides or shows the scrollbar of the documents body based on if the panel is open or not.
	 * Hides bar when panel is open and reshows bar when it's closed
	 */
	toggleScrollBar(isPanelOpen: boolean) {
		if (isPanelOpen) {
			this.renderer.setStyle(this.document.body, 'overflow', 'hidden');
		} else {
			this.renderer.setStyle(this.document.body, 'overflow', 'visible');
		}
	}

	/**
	 * @description Returns an object with the width, side, and duration for this instance of the component.
	 */
	getPanelStyle(): void {
		if (!this.showSidePanel) {
			return;
		}

		const panelStyle: KeyValue<string> = new KeyValue();
		const breakpoint = this.deviceService.getDeviceBreakPointNew();
		const isMobile = breakpoint <= BreakPointNew.SMALL;

		if (!isMobile) {
			panelStyle[
				'transition'
			] = `${this.side} ${this.duration}ms, visibility ${this.duration}ms`;
			panelStyle['width'] = `${this.width}%`;
			panelStyle[this.side] = '0%';
		} else {
			panelStyle['transition'] = `all 0.2s ease-out`;
			panelStyle['transform'] = `translate(-100%,0%)`;
			panelStyle['width'] = `${this.width}%`;
			panelStyle[this.side] = '-100%';
		}

		this.panelStyle = panelStyle;
	}

	/**
	 * @description Sets the panel header classes,
	 * based on if the panel is on the right or left side.
	 */
	getPanelHeaderClass(): void {
		if (!this.showSidePanel) {
			return;
		}
		if (this.side === 'left') {
			this.panelHeaderClass = 'flex-row-reverse left-side-panel';
		} else {
			this.panelHeaderClass = 'flex-row right-side-panel';
		}
	}

	/**
	 * @description
	 * determines if the device is mobile based on breakpoints when the window resizes
	 */
	@HostListener('window:resize', ['$event'])
	private onResize(): void {
		const breakpoint = this.deviceService.getDeviceBreakPointNew();
		this.isMobile = breakpoint <= BreakPointNew.SMALL;
	}
}
